import { BaseCard } from '@/components/uiKit/Cards';
import { useMemo } from 'react';

interface BeltHealthTrendProps {
  selectedItemId: string;
  selectedItemName: string;
  onDateClick: (date: string) => void;
  currentDate: string;
  historicalData: Record<string, Array<{ date: string; severity: string }>>;
  inspectionDates: string[];
  isClickEnabled?: boolean;
}

const getStatusColor = (status: string, isSelected: boolean) => {
  if (isSelected) {
    // When selected, show strong solid colors
    switch (status) {
      case 'critical':
        return '#CC0000'; // Strong red
      case 'alarm':
        return '#F97316'; // Orange
      case 'monitor':
        return '#EAB308'; // Yellow
      case 'healthy':
        return '#16A34A'; // Green
      case 'unknown':
      case 'no-data':
        return '#FFFFFF'; // White for no data
      default:
        return '#FFFFFF';
    }
  }

  // Unselected items - very light colors with opacity
  switch (status) {
    case 'critical':
      return 'rgba(204, 0, 0, 0.24)'; // Light red with opacity
    case 'alarm':
      return 'rgba(249, 115, 22, 0.24)'; // Light orange with opacity
    case 'monitor':
      return 'rgba(234, 179, 8, 0.24)'; // Light yellow with opacity
    case 'healthy':
      return 'rgba(22, 163, 74, 0.24)'; // Light green with opacity
    case 'unknown':
    case 'no-data':
      return '#FFFFFF'; // White
    default:
      return '#FFFFFF';
  }
};

export default function BeltHealthTrend({
  selectedItemId,
  selectedItemName,
  onDateClick,
  currentDate,
  historicalData,
  inspectionDates,
  isClickEnabled = true,
}: BeltHealthTrendProps) {
  const chartData = useMemo(() => {
    // Get health history for the selected item
    const itemHistory = historicalData[selectedItemId];

    if (!itemHistory) {
      // Return healthy as default if no history for item
      return inspectionDates.map((date) => ({
        date,
        status: 'healthy',
        isSelected: date === currentDate,
      }));
    }

    // Map dates to health status, default to healthy if no data for specific date
    return inspectionDates.map((date) => {
      const dateOnly = date.split('T')[0];
      const historyItem = itemHistory.find((h) => h.date === dateOnly);
      return {
        date,
        status: historyItem?.severity || 'healthy',
        isSelected: date === currentDate,
      };
    });
  }, [selectedItemId, currentDate, historicalData, inspectionDates]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate().toString().padStart(2, '0');
    return `${month} ${day}`;
  };

  return (
    <BaseCard
      variant="wrapper"
      title={`Health Trends for ${selectedItemName}`}
      classNames={{
        card: 'flex flex-col',
        content: 'h-[75px] bg-gray-50 rounded-lg p-0 overflow-hidden',
      }}
    >
      <div className="h-full bg-white rounded-lg relative">
        {/* Chart bars container with vertical grid lines */}
        <div className="h-[20px] mt-4 flex">
          {chartData.map((item, index) => {
            const isNoData = item.status === 'unknown' || item.status === 'no-data';
            return (
              <div
                key={index}
                className={`flex-1 relative
                  ${!isClickEnabled || isNoData ? 'cursor-default' : 'cursor-pointer hover:opacity-80'}
                `}
                onClick={() => isClickEnabled && !isNoData && onDateClick(item.date)}
              >
                {/* Bar rectangle - narrower width */}
                <div
                  className="h-full mx-auto rounded-sm"
                  style={{
                    backgroundColor: getStatusColor(item.status, item.isSelected),
                    width: '33.33%',
                  }}
                />
                {/* Vertical grid line on the right edge */}
                {index < chartData.length - 1 && <div className="absolute right-0 top-0 h-full w-px bg-gray-200" />}
              </div>
            );
          })}
        </div>

        {/* Date labels */}
        <div className="flex mt-2">
          {chartData.map((item, index) => (
            <div key={`label-${index}`} className="flex-1 text-center">
              <span className={`text-[9px] ${item.isSelected ? 'text-gray-700 font-semibold' : 'text-gray-500'}`}>
                {formatDate(item.date)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </BaseCard>
  );
}
